import { ChangeEvent, FC } from 'react'
import { useTranslation } from 'react-i18next'

import SearchRounded from '@mui/icons-material/SearchRounded'
import { Box, InputAdornment, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material'

import { GenreOption } from '../../hooks/useGamesListingFilter'

interface GamesListingFilterBarProps {
  search: string
  onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void
  genreFilter: string
  onGenreChange: (event: SelectChangeEvent<string>) => void
  genres: GenreOption[]
}

/**
 * Name search + genre dropdown rendered as the `SectionCard` title of the
 * tracked-game listing dialogs. Pair it with `useGamesListingFilter`, which
 * owns the state and the derived `genres` list.
 */
export const GamesListingFilterBar: FC<GamesListingFilterBarProps> = ({ search, onSearchChange, genreFilter, onGenreChange, genres }) => {
  const { t } = useTranslation('ua-insights')
  const allGenresLabel = t('all_genres')

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
      <TextField
        value={search}
        onChange={onSearchChange}
        placeholder={t('search_game_placeholder')}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRounded sx={{ color: 'text.secondary', fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
        sx={{ width: 240 }}
      />
      <Select
        value={genreFilter}
        onChange={onGenreChange}
        displayEmpty
        size="small"
        sx={{ minWidth: 160 }}
        renderValue={(selected) => genres.find((genre) => genre.id === selected)?.name ?? allGenresLabel}
      >
        <MenuItem value="">{allGenresLabel}</MenuItem>
        {genres.map((genre) => (
          <MenuItem key={genre.id} value={genre.id}>
            {genre.name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  )
}
